import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { formatNumber } from '@agro1desenvolvimento/utils-js';
import { UnidadeConversao } from '../../@types';
import { MixedInsumos, ProductOption, ResultMap, Substituicao } from './types';
import unidades from '../../services/unidade';
import saldosService from '../../services/saldos';
import Agro1Loader from '../Agro1Loader';
import useDropdownFilter from '../../hooks/use-dropdown-filter';

const NUMBER_FORMAT_OPTIONS = { maximumFractionDigits: 4, useGrouping: true };

const DOSE_REGEX = /^(\d+([.,]\d{0,4})?)?$/;

const productOptionTemplate = (option: ProductOption) => (
  <div className="item-saldo-option-template">
    <div className="option-label" title={option.label}>
      {option.label}
    </div>
    <div className="dropdown-item-saldo-container">
      <p className="dropdown-item-saldo">
        Físico: {formatNumber(option.saldoFisico, NUMBER_FORMAT_OPTIONS)}{' '}
        {option.unidadeAbreviatura}
      </p>
      <p className="dropdown-item-saldo">
        A receber: {formatNumber(option.saldoReceber, NUMBER_FORMAT_OPTIONS)}{' '}
        {option.unidadeAbreviatura}
      </p>
      <p className="dropdown-item-saldo">
        Em uso: {formatNumber(option.saldoEmUso, NUMBER_FORMAT_OPTIONS)}{' '}
        {option.unidadeAbreviatura}
      </p>
    </div>
  </div>
);

interface ProdutoDropdownProps {
  insumoId: string;
  substituicaoPor: string | null;
  propriedadeId: number | undefined;
  onSelectNewProduct: (
    insumoId: string,
    novoProdutoId: string | null,
    descricao: string | null,
  ) => void;
}

const ProdutoDropdown: React.FC<ProdutoDropdownProps> = ({
  insumoId,
  substituicaoPor,
  propriedadeId,
  onSelectNewProduct,
}) => {
  const { filterQuery, onFilter } = useDropdownFilter();
  const [opcoes, setOpcoes] = useState<ProductOption[]>([]);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (!propriedadeId) return;
    let cancelado = false;
    setCarregando(true);
    saldosService
      .saldos(propriedadeId, filterQuery)
      .then((resultado) => {
        if (!cancelado) {
          setOpcoes(
            resultado
              .filter((item) => String(item.id) !== insumoId)
              .map((item) => ({
                label: item.descricao,
                value: String(item.id),
                saldoFisico: item.saldoFisico,
                saldoReceber: item.saldoReceber,
                saldoEmUso: item.saldoEmUso,
                unidadeAbreviatura: item.unidade.abreviatura,
              })),
          );
        }
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [propriedadeId, filterQuery, insumoId]);

  return (
    <Dropdown
      value={substituicaoPor}
      options={opcoes}
      onChange={(evento) => {
        const selecionado = opcoes.find((o) => o.value === evento.value);
        onSelectNewProduct(insumoId, evento.value, selecionado?.label ?? null);
      }}
      placeholder="Pesquisar produto"
      filter
      onFilter={onFilter}
      dropdownIcon={carregando ? 'pi pi-spin pi-spinner' : undefined}
      itemTemplate={productOptionTemplate}
      virtualScrollerOptions={{ itemSize: 72 }}
      className="assistente-dropdown"
    />
  );
};

interface TabSubstituicaoProps {
  selectedInsumoObjects: MixedInsumos[];
  substituicoes: Record<string, Substituicao>;
  insumosUnicosMap?: ResultMap;
  propriedadeId: number | undefined;
  onUpdateSubstituicao: (
    id: string,
    field: keyof Omit<Substituicao, 'insumoId' | 'talhoes'>,
    value: string | null,
  ) => void;
}

const TabSubstituicao: React.FC<TabSubstituicaoProps> = ({
  selectedInsumoObjects,
  substituicoes,
  insumosUnicosMap = {},
  propriedadeId,
  onUpdateSubstituicao,
}) => {
  const [insumosCompativeis, setInsumosCompativeis] = useState<
    Record<number, UnidadeConversao[]>
  >({});

  const unidadesCarregadasRef = useRef<Set<number>>(new Set());

  const carregarUnidadesConversao = useCallback(async (unidadeId: number) => {
    if (unidadesCarregadasRef.current.has(unidadeId)) return;
    unidadesCarregadasRef.current.add(unidadeId);

    const identificadorLoader = Agro1Loader.show();
    try {
      const resultado = await unidades.listConversao(unidadeId);
      setInsumosCompativeis((anterior) => ({
        ...anterior,
        [unidadeId]: resultado,
      }));
    } catch (error) {
      console.error('Error calling listConversao:', error);
      unidadesCarregadasRef.current.delete(unidadeId);
    } finally {
      Agro1Loader.close(identificadorLoader);
    }
  }, []);

  const handleSelectNewProduct = useCallback(
    async (
      insumoId: string,
      novoProdutoId: string | null,
      descricao: string | null,
    ) => {
      onUpdateSubstituicao(insumoId, 'por', novoProdutoId);
      onUpdateSubstituicao(insumoId, 'porDescricao', descricao);

      if (novoProdutoId) {
        const novoProduto = insumosUnicosMap[novoProdutoId];
        if (novoProduto?.unidadesCompativeis?.length) {
          const primeiraUnidade = novoProduto.unidadesCompativeis[0];
          onUpdateSubstituicao(insumoId, 'unidade', String(primeiraUnidade.id));
          await carregarUnidadesConversao(primeiraUnidade.id);
        }
      }
    },
    [onUpdateSubstituicao, insumosUnicosMap, carregarUnidadesConversao],
  );

  useEffect(() => {
    Object.values(substituicoes).forEach((substituicao) => {
      if (substituicao?.por && substituicao?.unidade) {
        carregarUnidadesConversao(Number(substituicao.unidade));
      }
    });
  }, [substituicoes, carregarUnidadesConversao]);

  const opcoesUnidadePorInsumo = useMemo(
    () =>
      Object.fromEntries(
        selectedInsumoObjects.map((insumo) => {
          const substituicao = substituicoes[insumo.id];
          const opcoesConversao = substituicao?.unidade
            ? insumosCompativeis[Number(substituicao.unidade)] ?? []
            : [];
          return [
            insumo.id,
            opcoesConversao.map((unidade) => ({
              label: unidade.abreviatura,
              value: String(unidade.id),
            })),
          ];
        }),
      ),
    [selectedInsumoObjects, substituicoes, insumosCompativeis],
  );

  return (
    <div className="assistente-substituicao">
      <table className="assistente-table">
        <thead>
          <tr>
            <th>Substituir</th>
            <th>Por</th>
            <th>Dose</th>
            <th>Unidade</th>
          </tr>
        </thead>
        <tbody>
          {selectedInsumoObjects.map((insumo) => {
            const substituicao = substituicoes[insumo.id];
            const unidadeInsumo =
              insumo.unidadesCompativeis?.[0]?.abreviatura?.trim();
            const opcoesUnidade = opcoesUnidadePorInsumo[insumo.id] ?? [];

            return (
              <tr key={insumo.id}>
                <td className="assistente-table-col-source">
                  {`${insumo.nome}${unidadeInsumo ? `/${unidadeInsumo}` : ''}`}
                </td>
                <td className="assistente-table-col-dropdown">
                  <ProdutoDropdown
                    insumoId={insumo.id}
                    substituicaoPor={substituicao?.por ?? null}
                    propriedadeId={propriedadeId}
                    onSelectNewProduct={handleSelectNewProduct}
                  />
                </td>
                <td className="assistente-table-col-dose">
                  <InputText
                    value={substituicao?.dose ?? ''}
                    onChange={(evento) => {
                      if (!DOSE_REGEX.test(evento.target.value)) return;
                      onUpdateSubstituicao(
                        insumo.id,
                        'dose',
                        evento.target.value,
                      );
                    }}
                    placeholder="Dose"
                    className="assistente-dose-input"
                  />
                </td>
                <td className="assistente-table-col-unidade">
                  <Dropdown
                    value={substituicao?.unidade ?? null}
                    options={opcoesUnidade}
                    onChange={(evento) =>
                      onUpdateSubstituicao(insumo.id, 'unidade', evento.value)
                    }
                    placeholder="Unidade"
                    className="assistente-dropdown assistente-unidade-dropdown"
                    disabled={!opcoesUnidade.length}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(TabSubstituicao);
